import {
  type DefectDetail,
  type DefectPhoto,
  type PortalResponse,
  defectDetailSchema,
  defectPhotoSchema,
  portalResponseSchema,
} from '@sivan/shared';
import { useRef, useState } from 'react';
import { ApiError, NetworkError, apiGet, apiPost, apiUpload } from '../api/client';
import { PhotoPrepareError, preparePhoto } from '../handover/prepare-photo';
import { LoadingBlock, Problem } from '../handover/common';
import { useLoad } from '../master-data/use-master-data';
import { navigate, useHash } from '../routing';
import { EmptyState } from '../ui/EmptyState';
import { Icon } from '../ui/Icon';
import {
  DefectCard,
  PhotoStrip,
  RepairHistory,
  RepairStatusBadge,
  formatDate,
  photoSrc,
} from './parts';

type Tab = 'todo' | 'waiting' | 'done';
const TABS: [Tab, string][] = [
  ['todo', 'דורשים טיפול'],
  ['waiting', 'ממתינים לאישור'],
  ['done', 'הושלמו'],
];
const inTab = (tab: Tab, status: string) =>
  tab === 'todo'
    ? status === 'open' || status === 'in_progress'
    : tab === 'waiting'
      ? status === 'fixed'
      : status === 'verified';

/** הליקויים שלי (DR-196): a supplier's own defects, and reporting a repair with photos. */
export function PortalScreen() {
  const parts = useHash().replace(/^#/, '').split('/');
  const findingId = parts[0] === 'portal' ? (parts[1] ?? null) : null;
  return findingId === null || findingId === '' ? (
    <PortalHome />
  ) : (
    <PortalDefect key={findingId} findingId={findingId} />
  );
}

function PortalHome() {
  const [loaded, reload] = useLoad('portal', () => apiGet('/portal/defects', portalResponseSchema));
  const [tab, setTab] = useState<Tab>('todo');
  if (loaded.state === 'loading') return <LoadingBlock />;
  if (loaded.state === 'forbidden')
    return (
      <EmptyState icon="lock" title="אין לך עדיין גישה לליקויים">
        <p>נציג סיון ביצוע ישייך אותך לפרויקט. לאחר מכן הליקויים שלך יופיעו כאן.</p>
      </EmptyState>
    );
  if (loaded.state === 'error')
    return <Problem text="טעינת הליקויים נכשלה." requestId={loaded.requestId} onRetry={reload} />;
  const data: PortalResponse = loaded.data;
  const items = data.items.filter((d) => inTab(tab, d.status));
  return (
    <div className="portal">
      <p className="portal__who">
        <Icon name="building" /> {data.supplier.name}
      </p>
      <div className="portal-tabs" role="tablist" aria-label="הליקויים שלי">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className="portal-tab"
            onClick={() => {
              setTab(key);
            }}
          >
            <b>{data.counts[key]}</b>
            <span>{label}</span>
          </button>
        ))}
      </div>
      {items.length === 0 ? (
        <EmptyState
          icon="circle-check"
          title={tab === 'todo' ? 'אין ליקויים לטיפול' : 'אין כאן ליקויים'}
        >
          <p>
            {tab === 'todo' ? 'כל הליקויים שלך טופלו. כל הכבוד.' : 'ליקויים יופיעו כאן כשיהיו.'}
          </p>
        </EmptyState>
      ) : (
        <ul className="defect-list" role="tabpanel">
          {items.map((d) => (
            <DefectCard
              key={d.id}
              defect={d}
              href={`#portal/${d.id}`}
              showProject
              action={tab === 'todo' ? 'לטיפול' : 'פרטים'}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

const explain = (error: unknown): string => {
  if (error instanceof NetworkError) return 'אין חיבור לשרת. נסו שוב כשיש קליטה.';
  if (error instanceof PhotoPrepareError) return 'לא ניתן לקרוא את התמונה. נסו לצלם שוב.';
  if (error instanceof ApiError) {
    const code = (error.body['conflict'] as { code?: string } | undefined)?.code;
    if (code === 'stale' || code === 'repair_state') return 'מצב הליקוי השתנה. הדף רוענן, נסו שוב.';
    if (code === 'repair_photo') return 'יש לצרף לפחות תמונה אחת של התיקון.';
    if (code === 'repair_photo_limit') return 'הגעתם למספר התמונות המרבי לליקוי.';
    if (error.status === 413) return 'התמונה גדולה מדי.';
  }
  return 'הפעולה נכשלה. נסו שוב.';
};

function PortalDefect({ findingId }: { readonly findingId: string }) {
  const [loaded, reload] = useLoad(`portal:${findingId}`, () =>
    apiGet(`/portal/defects/${findingId}`, defectDetailSchema),
  );
  const [doc, setDoc] = useState<DefectDetail | null>(null);
  const back = () => {
    navigate('#portal', 'push');
  };
  if (loaded.state === 'loading') return <LoadingBlock />;
  if (loaded.state !== 'ready')
    return <Problem text="הליקוי לא נמצא או שאינו משויך אליכם." onRetry={back} />;
  const detail = doc ?? loaded.data;
  return (
    <DefectForSupplier
      detail={detail}
      onBack={back}
      onChanged={setDoc}
      onStale={() => {
        setDoc(null);
        reload();
      }}
    />
  );
}

function DefectForSupplier({
  detail,
  onBack,
  onChanged,
  onStale,
}: {
  readonly detail: DefectDetail;
  readonly onBack: () => void;
  readonly onChanged: (next: DefectDetail) => void;
  readonly onStale: () => void;
}) {
  const { defect, actions } = detail;
  const [photos, setPhotos] = useState<DefectPhoto[]>(detail.pendingPhotos);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const base = `/portal/defects/${defect.id}`;

  const fail = (error: unknown) => {
    setMessage({ kind: 'error', text: explain(error) });
    if (error instanceof ApiError && error.status === 409) onStale();
  };
  const upload = async (files: FileList | null) => {
    if (files === null || files.length === 0) return;
    setMessage(null);
    for (const file of Array.from(files)) {
      try {
        setUploading(0);
        const blob = await preparePhoto(file);
        const photo = await apiUpload(`${base}/photos`, blob, defectPhotoSchema, setUploading);
        setPhotos((list) => [...list, photo]);
      } catch (error) {
        fail(error);
        break;
      } finally {
        setUploading(null);
      }
    }
  };
  const post = (path: string, body: Record<string, unknown>, done: string) => {
    setBusy(true);
    setMessage(null);
    apiPost(`${base}/${path}`, { rowVersion: defect.rowVersion, ...body }, defectDetailSchema)
      .then((next) => {
        onChanged(next);
        setPhotos(next.pendingPhotos);
        setNote('');
        setMessage({ kind: 'success', text: done });
      })
      .catch(fail)
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <article className="defect-detail portal-defect" aria-labelledby="portal-title">
      <button type="button" className="button button--ghost back-button" onClick={onBack}>
        → הליקויים שלי
      </button>
      <header className="defect-detail__head">
        <p className="hint">
          {defect.projectName} · {defect.building} · דירה {defect.apartment}
        </p>
        <h2 id="portal-title">
          {defect.area} · {defect.component}
        </h2>
        <p className="defect-detail__text">{defect.description}</p>
        <p className="defect-detail__meta">
          <RepairStatusBadge status={defect.status} />
          {defect.workCategory !== null && (
            <span className="defect-chip">{defect.workCategory}</span>
          )}
          {defect.dueOn !== null && (
            <span className={`defect-chip${defect.overdue ? ' defect-chip--late' : ''}`}>
              {defect.overdue ? 'באיחור · ' : 'יעד '}
              {formatDate(defect.dueOn)}
            </span>
          )}
        </p>
        {actions.report && photos.length === 0 && (
          // The one next step, at hand: the camera opens at once.
          <button
            type="button"
            className="button button--primary portal-defect__cta"
            disabled={uploading !== null || busy}
            onClick={() => camera.current?.click()}
          >
            <Icon name="camera" /> צילום התיקון
          </button>
        )}
      </header>
      {message !== null && (
        <p
          className={`notice notice--${message.kind}`}
          role={message.kind === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      )}

      <section aria-labelledby="portal-photos">
        <h3 id="portal-photos">תמונות הליקוי</h3>
        <PhotoStrip photos={detail.photos} label="תמונות הליקוי" />
      </section>
      {actions.report ? (
        <section className="repair-report" aria-labelledby="portal-report">
          <h3 id="portal-report">דיווח על תיקון</h3>
          <ol className="repair-steps">
            <li className={photos.length > 0 ? 'done' : ''}>
              <b>1. צילום התיקון</b>
              <span className="hint">חובה: לפחות תמונה אחת שמראה שהליקוי תוקן.</span>
              {photos.length > 0 && (
                <ul className="defect-photos" aria-label="תמונות התיקון שצירפתם">
                  {photos.map((p, i) => (
                    <li key={p.id}>
                      <img src={photoSrc(p)} alt={`תמונת תיקון ${String(i + 1)}`} />
                    </li>
                  ))}
                </ul>
              )}
              <div className="repair-steps__buttons">
                <button
                  type="button"
                  className={`button ${photos.length === 0 ? 'button--primary' : ''}`}
                  disabled={uploading !== null || busy}
                  onClick={() => camera.current?.click()}
                >
                  <Icon name="camera" /> {photos.length === 0 ? 'צילום התיקון' : 'צילום נוסף'}
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  disabled={uploading !== null || busy}
                  onClick={() => gallery.current?.click()}
                >
                  <Icon name="images" /> מהגלריה
                </button>
              </div>
              {uploading !== null && (
                <p className="hint" role="status">
                  מעלה תמונה… {Math.round(uploading * 100)}%
                </p>
              )}
              <input
                ref={camera}
                className="visually-hidden"
                type="file"
                accept="image/*"
                capture="environment"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(e) => {
                  void upload(e.target.files);
                  e.target.value = '';
                }}
              />
              <input
                ref={gallery}
                className="visually-hidden"
                type="file"
                accept="image/jpeg,image/png"
                multiple
                tabIndex={-1}
                aria-hidden="true"
                onChange={(e) => {
                  void upload(e.target.files);
                  e.target.value = '';
                }}
              />
            </li>
            <li>
              <label className="field">
                <b>2. הערה קצרה (רשות)</b>
                <textarea
                  rows={2}
                  maxLength={1000}
                  value={note}
                  placeholder="למשל: הוחלף אטם בברז"
                  onChange={(e) => {
                    setNote(e.target.value);
                  }}
                />
              </label>
            </li>
          </ol>
          <div className="repair-report__submit">
            <button
              type="button"
              className="button button--primary"
              disabled={busy || uploading !== null || photos.length === 0}
              onClick={() => {
                post(
                  'report',
                  { photoIds: photos.map((p) => p.id), note },
                  'התיקון נשלח לאישור סיון ביצוע.',
                );
              }}
            >
              <Icon name="check" /> {busy ? 'שולח…' : 'שליחה לאישור'}
            </button>
            {photos.length === 0 && <p className="hint">צלמו את התיקון כדי לשלוח.</p>}
          </div>
          {actions.start && (
            <button
              type="button"
              className="button button--ghost"
              disabled={busy}
              onClick={() => {
                post('start', {}, 'סומן שהטיפול התחיל.');
              }}
            >
              <Icon name="clock" /> סימון: התחלתי לטפל
            </button>
          )}
        </section>
      ) : (
        <p className="notice notice--info">
          {defect.status === 'fixed'
            ? 'הדיווח נשלח. נציג סיון ביצוע יבדוק את התיקון.'
            : 'הליקוי אושר ונסגר. תודה.'}
        </p>
      )}
      <section aria-labelledby="portal-history">
        <h3 id="portal-history">היסטוריה</h3>
        <RepairHistory history={detail.history} />
      </section>
    </article>
  );
}
